import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtBRL, fmtDate, todayISO } from '@/lib/format';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, FileBarChart } from 'lucide-react';

function csvDownload(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? '')).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ReportsPage() {
  const first = todayISO().slice(0, 8) + '01';
  const [range, setRange] = useState({ date_from: first, date_to: todayISO() });

  const cashflow = useQuery({
    queryKey: ['report-cashflow', range],
    queryFn: async () => (await api.get('/reports/cashflow', { params: range })).data,
  });
  const byCatExp = useQuery({
    queryKey: ['report-cat', range, 'EXPENSE'],
    queryFn: async () => (await api.get('/reports/by-category', { params: { ...range, type: 'EXPENSE' } })).data,
  });
  const byCatInc = useQuery({
    queryKey: ['report-cat', range, 'INCOME'],
    queryFn: async () => (await api.get('/reports/by-category', { params: { ...range, type: 'INCOME' } })).data,
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Analytics</div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Relatórios</h1>
      </div>
      <Card className="p-4 flex items-end gap-3 flex-wrap border-border/70">
        <div className="space-y-1"><Label className="text-xs">De</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">Até</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></div>
      </Card>

      <Tabs defaultValue="cashflow">
        <TabsList>
          <TabsTrigger value="cashflow">Fluxo de Caixa</TabsTrigger>
          <TabsTrigger value="expense">Despesas por Categoria</TabsTrigger>
          <TabsTrigger value="income">Receitas por Categoria</TabsTrigger>
        </TabsList>

        <TabsContent value="cashflow" className="mt-4">
          <Card className="p-5 border-border/70">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold">Fluxo de Caixa</h3>
              <Button size="sm" variant="outline" onClick={() => csvDownload(cashflow.data?.rows, 'fluxo-caixa.csv')}><Download className="h-4 w-4 mr-1" /> CSV</Button>
            </div>
            {cashflow.isLoading ? 'Carregando…' : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <Card className="p-3"><div className="text-[10px] uppercase font-mono text-muted-foreground">Total Entradas</div><div className="font-mono tabular-nums text-xl font-semibold money-in">{fmtBRL(cashflow.data?.total_income)}</div></Card>
                  <Card className="p-3"><div className="text-[10px] uppercase font-mono text-muted-foreground">Total Saídas</div><div className="font-mono tabular-nums text-xl font-semibold money-out">{fmtBRL(cashflow.data?.total_expense)}</div></Card>
                  <Card className="p-3"><div className="text-[10px] uppercase font-mono text-muted-foreground">Resultado</div><div className={`font-mono tabular-nums text-xl font-semibold ${(cashflow.data?.net || 0) >= 0 ? 'money-in' : 'money-out'}`}>{fmtBRL(cashflow.data?.net)}</div></Card>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b"><tr className="text-left text-[10px] uppercase font-mono text-muted-foreground"><th className="px-4 py-2">Data</th><th className="px-4 py-2 text-right">Entradas</th><th className="px-4 py-2 text-right">Saídas</th><th className="px-4 py-2 text-right">Líquido</th></tr></thead>
                    <tbody>
                      {(cashflow.data?.rows || []).map((r) => (
                        <tr key={r.date} className="border-b border-border/50">
                          <td className="px-4 py-2 font-mono">{fmtDate(r.date)}</td>
                          <td className="px-4 py-2 text-right font-mono money-in">{fmtBRL(r.income)}</td>
                          <td className="px-4 py-2 text-right font-mono money-out">{fmtBRL(r.expense)}</td>
                          <td className={`px-4 py-2 text-right font-mono font-semibold ${r.net >= 0 ? 'money-in' : 'money-out'}`}>{fmtBRL(r.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        {[{ key: 'expense', data: byCatExp, label: 'Despesas por Categoria', file: 'despesas-categoria.csv' },
          { key: 'income', data: byCatInc, label: 'Receitas por Categoria', file: 'receitas-categoria.csv' }].map(({ key, data, label, file }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <Card className="p-5 border-border/70">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-semibold">{label}</h3>
                <Button size="sm" variant="outline" onClick={() => csvDownload(data.data?.rows, file)}><Download className="h-4 w-4 mr-1" /> CSV</Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b"><tr className="text-left text-[10px] uppercase font-mono text-muted-foreground"><th className="px-4 py-2">Categoria</th><th className="px-4 py-2 text-right">Qtd</th><th className="px-4 py-2 text-right">Total</th></tr></thead>
                <tbody>
                  {(data.data?.rows || []).map((r) => (
                    <tr key={r.category_id} className="border-b border-border/50">
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2 text-right font-mono">{r.count}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{fmtBRL(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
